import { useMemo } from 'react';

import { useAppSelector } from 'store/store-hooks';
import { ClipOrder } from 'services/story.service';
import { getClipTime, getRandomizedArray } from 'components/app/_helpers/_app.helper';
import { getSbOrderClips } from 'components/app/_helpers/app-config.helper';

export const _useConfigOrder = () => {
  const { storyBoard, clipsMap, linkedClips } = useAppSelector((state) => state.editor);
  const files = useAppSelector((state) => state.app__files.files);

  const sbLinkArray = useMemo(() => {
    const sbLinkArray = [...storyBoard.storyBoard];
    const linkedClipsArray = Object.values(linkedClips) as ClipId[][];
    linkedClipsArray.forEach((linkedClips: ClipId[]) => {
      const indexFirstLinked = sbLinkArray.indexOf(linkedClips[0]);
      if (indexFirstLinked !== -1) {
        sbLinkArray.splice(indexFirstLinked, linkedClips.length, linkedClips as unknown as ClipId);
      }
    });
    return sbLinkArray.map((item) => (Array.isArray(item) ? item : [item]));
  }, [storyBoard, linkedClips]);

  const chronologicalSort = () => {
    const clipsWithDateTime = sbLinkArray.map((sbLinkedClips: CellClip[]) => {
      const clipData = (sbLinkedClips[0] && clipsMap && clipsMap[sbLinkedClips[0]]) || null;
      const mediaFileId = clipData?.mediaFileId;
      return {
        mediaOriginDate: (mediaFileId && files[mediaFileId].mediaOriginDate) || '',
        clips: sbLinkedClips,
        videoTime: clipData ? getClipTime(clipData) : -1,
      };
    });

    const sortedByVideoTime = clipsWithDateTime.sort((A, B) => {
      const isValidDate = (date: Date) => !isNaN(date.getTime());
      const dateA = new Date(A?.mediaOriginDate);
      const dateB = new Date(B?.mediaOriginDate);
      const validA = isValidDate(dateA);
      const validB = isValidDate(dateB);
      if (validA && validB) return dateA.getTime() - dateB.getTime();
      if (!validA && validB) return 1;
      if (validA && !validB) return -1;
      return A.videoTime - B.videoTime || 0;
    });

    return sortedByVideoTime.map((element) => element.clips);
  };

  const firstLastRandomOrder = () => {
    const firstClips: CellClip[] = [];
    const lastClips: CellClip[] = [];
    const { firstClipOrder, lastClipOrder } = getSbOrderClips(clipsMap);

    const noExtremeClips = getRandomizedArray(sbLinkArray).filter(
      (sbCellClips: CellClip[]) =>
        !sbCellClips.find((i) => {
          if (i === firstClipOrder) {
            firstClips.unshift(...sbCellClips);
            return true;
          }
          if (i === lastClipOrder) {
            lastClips.push(...sbCellClips);
            return true;
          }
        })
    );
    return [firstClips, ...noExtremeClips, lastClips];
  };

  return (clipOrder: ClipOrder) => {
    switch (clipOrder) {
      case ClipOrder.chronological:
        return chronologicalSort().flat();
      case ClipOrder.random:
        return getRandomizedArray(sbLinkArray).flat();
      case ClipOrder.maintainFirstLast:
        return firstLastRandomOrder().flat();
    }
  };
};
